import React from "react";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useOrders } from "@/hooks/useOrders";
import { clearCart } from "@/store/slices/cartSlice";
import { setOrderState } from "@/store/slices/uiSlice";
import { handleValidationError } from "@/utils/toast";

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const steps = ["Shipping", "Payment", "Review"];
  const [activeStep, setActiveStep] = React.useState(0);
  const items = useSelector((s) => s.cart.items);
  const subtotal = items.reduce(
    (sum, i) => sum + (i.price || 0) * i.quantity,
    0
  );
  const shipping = 0;
  const tax = 0;
  const total = subtotal + shipping + tax;

  const [form, setForm] = React.useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "IN",
  });
  const [errors, setErrors] = React.useState({});
  const [order, setOrder] = React.useState(null);

  // API hooks
  const {
    loading,
    error,
    createNewOrder,
    initiateRazorpayPayment,
    verifyPayment,
  } = useOrders();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateShipping = () => {
    const e = {};
    if (!form.customerName.trim()) e.customerName = "Full name is required";
    if (!form.customerEmail.trim()) e.customerEmail = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerEmail))
      e.customerEmail = "Enter a valid email";
    if (!form.street.trim()) e.street = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.postalCode.trim()) e.postalCode = "Postal code is required";
    if (!form.country.trim()) e.country = "Country is required";
    setErrors(e);

    if (Object.keys(e).length > 0) {
      handleValidationError(e);
    }

    return Object.keys(e).length === 0;
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePay = async () => {
    if (!validateShipping()) return;

    try {
      // Create order with backend schema
      const orderPayload = {
        orderItems: items.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
          price: i.price,
        })),
        totalAmount: total,
        paymentMethod: "razorpay",
        customerName: form.customerName,
        shippingAddress: {
          customerEmail: form.customerEmail,
          customerName: form.customerName,
          street: form.street,
          city: form.city,
          state: form.state,
          postalCode: form.postalCode,
          country: form.country,
        },
        isPreorder: items.some((i) => i.isPreorder),
      };

      const createdOrder = await createNewOrder(orderPayload);
      setOrder(createdOrder);

      const ok = await loadRazorpay();
      if (!ok) {
        // Toast notification is handled by the error handling in useOrders
        return;
      }

      const razorpayOrderData = await initiateRazorpayPayment(createdOrder._id);

      const options = {
        key: razorpayOrderData.key_id,
        amount: razorpayOrderData.razorpayOrder.amount,
        currency: razorpayOrderData.razorpayOrder.currency || "INR",
        name: "Kidilan Traveler",
        description: "Order Payment",
        order_id: razorpayOrderData.razorpayOrder.id,
        handler: async (response) => {
          try {
            const verificationData = {
              orderId: createdOrder._id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };

            const verificationResult = await verifyPayment(verificationData);

            if (verificationResult.success) {
              console.log(
                "Payment successful, setting order state:",
                verificationResult.payment.orderId
              );
              // Set order state before navigation to allow access to order success page
              // Use the orderId from the payment verification response
              dispatch(
                setOrderState({
                  orderId: verificationResult.payment.orderId,
                  isValid: true,
                })
              );

              sessionStorage.setItem(
                "orderState",
                JSON.stringify({
                  orderId: verificationResult.payment.orderId,
                  isValid: true,
                })
              );

              console.log("Order state set, navigating to success page");
              navigate("/order-success");
            } else {
              // Error toast is handled by the verifyPayment function
            }
          } catch (error) {
            console.error("Payment verification failed:", error);
            // Error toast is handled by the verifyPayment function
          }
        },
        modal: {
          ondismiss: () => {
            // Handle modal dismiss
          },
        },
        prefill: {
          name: form.customerName,
          email: form.customerEmail,
          contact: form.customerPhone,
        },
        notes: {
          orderId: createdOrder._id,
        },
        theme: { color: "#27323d" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Checkout failed", error);
      // Error toast is handled by the useOrders hook
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));
  const handleContinue = () => {
    if (activeStep === 0) {
      if (validateShipping()) setActiveStep(1);
      return;
    }
    if (activeStep === 1) {
      handlePay();
      return;
    }
  };

  if (items.length === 0) {
    return (
      <div className="bg-white text-slate-900 min-h-screen">
        <div className="container py-10 text-center">
          <h1 className="font-display text-3xl mb-6">Your cart is empty</h1>
          <Button variant="contained" onClick={() => navigate("/shop")}>
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-slate-900 min-h-screen">
      <div className="container py-10">
        <h1 className="font-display text-3xl mb-6 text-center">Checkout</h1>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div className="mt-8 bg-white rounded-2xl p-6 max-w-3xl mx-auto shadow">
          {activeStep === 0 && (
            <div className="space-y-4">
              <Typography variant="h6" className="mb-4">
                Shipping Information
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="Full Name"
                  variant="outlined"
                  fullWidth
                  value={form.customerName}
                  onChange={update("customerName")}
                  error={!!errors.customerName}
                  helperText={errors.customerName || ""}
                />
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  value={form.customerEmail}
                  onChange={update("customerEmail")}
                  error={!!errors.customerEmail}
                  helperText={errors.customerEmail || ""}
                />
                <TextField
                  label="Phone"
                  variant="outlined"
                  fullWidth
                  value={form.customerPhone}
                  onChange={update("customerPhone")}
                />
                <TextField
                  label="Country"
                  variant="outlined"
                  fullWidth
                  value={form.country}
                  onChange={update("country")}
                  error={!!errors.country}
                  helperText={errors.country || ""}
                />
                <TextField
                  label="Address"
                  variant="outlined"
                  fullWidth
                  className="md:col-span-2"
                  value={form.street}
                  onChange={update("street")}
                  error={!!errors.street}
                  helperText={errors.street || ""}
                />
                <TextField
                  label="City"
                  variant="outlined"
                  fullWidth
                  value={form.city}
                  onChange={update("city")}
                  error={!!errors.city}
                  helperText={errors.city || ""}
                />
                <TextField
                  label="State"
                  variant="outlined"
                  fullWidth
                  value={form.state}
                  onChange={update("state")}
                />
                <TextField
                  label="Postal Code"
                  variant="outlined"
                  fullWidth
                  value={form.postalCode}
                  onChange={update("postalCode")}
                  error={!!errors.postalCode}
                  helperText={errors.postalCode || ""}
                />
              </div>
            </div>
          )}

          {activeStep === 1 && (
            <div className="space-y-4 text-center">
              <Typography variant="h6" className="mb-4">
                Payment
              </Typography>
              <Typography variant="body1">
                You will be redirected to Razorpay to complete your payment.
              </Typography>

              {/* Order Summary */}
              <div className="rounded-2xl border border-slate-100 p-4 text-left max-w-md mx-auto">
                <Typography variant="subtitle1" className="mb-3 font-semibold">
                  Order Summary
                </Typography>
                {items.map((item) => (
                  <div
                    key={`${item.productId}-${item.variant || ""}`}
                    className="flex justify-between items-center py-2"
                  >
                    <div className="flex-1">
                      <div className="text-sm">{item.name}</div>
                      <div className="text-xs text-slate-500">
                        Qty: {item.quantity}
                      </div>
                    </div>
                    <div className="text-sm font-medium">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
                <Divider className="!my-3" />
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="text-slate-900 font-medium">
                    ₹{subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Shipping</span>
                  <span className="text-slate-900 font-medium">Free</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Tax</span>
                  <span className="text-slate-900 font-medium">
                    ₹{tax.toFixed(2)}
                  </span>
                </div>
                <Divider className="!my-2" />
                <div className="flex items-center justify-between text-base">
                  <span className="text-slate-900 font-semibold">Total</span>
                  <span className="text-slate-900 font-semibold">
                    ₹{total.toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                variant="contained"
                color="primary"
                onClick={handlePay}
                disabled={loading}
                size="large"
              >
                {loading ? "Processing..." : "Pay now"}
              </Button>
              <div className="text-slate-500 text-xs">
                Secure payment powered by Razorpay
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <Button disabled={activeStep === 0 || loading} onClick={handleBack}>
              Back
            </Button>
            {activeStep === 0 && (
              <Button variant="contained" onClick={handleContinue}>
                Continue to Payment
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
